/**
 * เส้นทาง **VOLUNTARY**: ผู้ที่เข้าสู่ระบบแล้วเปลี่ยนรหัสผ่านของตัวเอง
 *
 * ต่างจากเส้นทางกู้คืนบนหน้า Login สองข้อ และทั้งสองข้อมาจากเซิร์ฟเวอร์:
 *
 * 1. **token ที่ใช้คือ session token ปกติ** จาก `session.ts` ไม่ใช่ token ที่จำกัด
 *    ขอบเขตของ D-050
 * 2. **เปลี่ยนสำเร็จแล้ว session ของผู้เรียกยังอยู่** (`docs/API_CONTRACT.md`:
 *    "เปลี่ยนสำเร็จแล้ว session ของผู้เรียกยังอยู่ ส่วน session อื่นของคนเดียวกัน
 *    ถูก revoke ทั้งหมด") panel นี้จึง **ห้ามเรียก `endSession()`** ไม่ว่ากรณีใด —
 *    การเตะผู้ใช้กลับไปหน้า Login หลังเปลี่ยนรหัสสำเร็จ จะแยกไม่ออกจากการเปลี่ยนที่
 *    ล้มเหลว และเป็นเหตุให้คนหน้างานพิมพ์รหัสใหม่ทันทีบนเครื่องที่วางอยู่กลางโรงงาน
 *
 * panel นี้ไม่ผูกกับ shell ไหน: `onClose` เป็นของผู้เรียก เพราะ "กลับไปที่เดิม"
 * แปลว่าคนละที่สำหรับ Desktop shell กับเครื่องสแกน
 */

import { useState } from 'react';

import { ChangePasswordForm } from './ChangePasswordForm';
import type { DeviceKind } from './client';
import './changePassword.css';
import { useSession } from './session';

export const VOLUNTARY_CHANGE_DONE_MESSAGE =
  'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว — เซสชันนี้ยังใช้งานได้ต่อ ส่วนอุปกรณ์อื่นที่เข้าสู่ระบบด้วยบัญชีนี้ถูกตัดออกจากระบบแล้ว';

export interface ChangeOwnPasswordPanelProps {
  /** มาจาก shell ที่กำลังใช้อยู่ ไม่ใช่จากผู้ใช้ */
  readonly deviceKind: DeviceKind;
  /** ปิด panel แล้วกลับไปหน้าจอเดิมของ shell นั้น */
  readonly onClose?: () => void;
  /** inject ได้เพื่อการทดสอบ — production ใช้ค่าจาก `VITE_API_BASE_URL` */
  readonly apiBaseUrl?: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

export function ChangeOwnPasswordPanel({
  deviceKind,
  onClose,
  apiBaseUrl,
  fetchImpl,
}: ChangeOwnPasswordPanelProps) {
  const { token } = useSession();
  const [done, setDone] = useState(false);

  if (token === null) {
    // ไม่มี token = ยังไม่ได้เข้าสู่ระบบ จึงไม่มีรหัสผ่านของ "ตัวเอง" ให้เปลี่ยน
    // deny-safe: ไม่เรนเดอร์ฟอร์มที่ไม่มีอะไรไปยื่นให้เซิร์ฟเวอร์
    return null;
  }

  return (
    <div className="cap-password-change-panel" data-testid="change-own-password">
      {done ? (
        <div className="cap-password-change__done" role="status" data-testid="password-change-done">
          <strong>เปลี่ยนรหัสผ่านแล้ว</strong>
          <span>{VOLUNTARY_CHANGE_DONE_MESSAGE}</span>
          {onClose === undefined ? null : (
            <button
              type="button"
              className="cap-password-change__cancel"
              data-testid="password-change-done-close"
              onClick={onClose}
            >
              กลับไปหน้าจอเดิม
            </button>
          )}
        </div>
      ) : (
        <ChangePasswordForm
          purpose="VOLUNTARY"
          token={token}
          deviceKind={deviceKind}
          onChanged={() => {
            // ไม่แตะ session store เลย — เซิร์ฟเวอร์เก็บ session ของผู้เรียกไว้ให้
            setDone(true);
          }}
          {...(onClose === undefined ? {} : { onCancel: onClose })}
          {...(apiBaseUrl === undefined ? {} : { apiBaseUrl })}
          {...(fetchImpl === undefined ? {} : { fetchImpl })}
        />
      )}
    </div>
  );
}
